import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  updateSetting,
  SettingData,
  SettingProps,
} from '@/account/useUserSetting';
import { useSession } from '@/auth/index';
import {
  signForm,
  SignFormProps,
} from '@/compliance/services/nonComplianceService';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  Text,
  Input,
  RadioGroup,
  FormControl,
  Radio,
  FormControlLabel,
  Link,
  Icons,
  styled,
} from '@/theme/index';
import { useRedirect } from '@/utils/hooks/useRedirect';
import { schemaComplianceForm } from '@/utils/schemaForms/schemaForms';
import { ComplianceFormFooter } from './ComplianceFormFooter';

type ComplianceFormQuestionProps = {
  userName: string;
  grantAccessToAccountWithoutCriminalData?: boolean;
};

const QuestionTag = styled(Text)(({ theme }) => ({
  textAlign: 'left',
  fontWeight: 'bold',
  margin: `${theme.spacing(1)} 0 ${theme.spacing(1)} 0`,
  borderRadius: theme.spacing(0.5),
}));

const QuestionContainter = styled('div')(({ theme }) => ({
  margin: `${theme.spacing(1.5)} auto`,
  width: '90%',

  [theme.breakpoints.up('sm')]: {
    width: '50%',
  },
}));

const SignatureLabelTag = styled(Text)(({ theme }) => ({
  fontfamily: 'Lato-LightItalic Lato Light Italic  Lato',

  [theme.breakpoints.up('sm')]: {
    width: '80%',
  },
}));

const InputTag = styled(Input)(({ theme }) => ({
  '&.MuiFormControl-root': {
    display: 'block',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.secondary.main,
    border: '0',
    color: theme.palette.secondary.contrastText,
    fontSize: theme.spacing(2.5),
    borderRadius: theme.spacing(0.5),
    height: '52px',
    display: 'block',
    width: '100%',
    transition: 'border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s',
  },
  '& .MuiInputBase-input': {
    padding: `${theme.spacing(1)} 0 0 ${theme.spacing(1.25)}`,
    width: '98%',
  },
}));

export function ComplianceFormQuestion({
  userName,
  grantAccessToAccountWithoutCriminalData = true,
}: ComplianceFormQuestionProps) {
  const {
    data: { brandName },
    links: { tosLink },
    featureFlags: { hasCriminalEnabled },
  } = useAppConstantsContext();
  const [isSigning, setIsSigning] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [criminalRecordsAgreement, setCriminalRecordsAgreement] =
    useState<string>('agree');
  const { redirect } = useRedirect();
  const { logError } = useAppConfigContext();
  const {
    session: { account },
    setSession,
  } = useSession();

  const userSettingQuery = useMutation<SettingData, Error, SettingProps>({
    mutationFn: updateSetting,

    onSuccess: () => {
      // NOTE: Because this won't be null at this point of the flow
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      account!.account.user_settings.hide_criminal_records =
        grantAccessToAccountWithoutCriminalData;
      account!.account.user_info.noncompliant = false;
      setSession({ account });

      if (grantAccessToAccountWithoutCriminalData) {
        const redirectOptions = {
          reject: '/compliance-permitted',
          agree: '/compliance-recomply',
        };

        const redirectTo =
          redirectOptions[
            criminalRecordsAgreement as keyof typeof redirectOptions
          ];

        redirect(redirectTo);
      } else {
        redirect('/dashboard');
      }
    },
    onError: (error) => {
      logError('User Setting Update', error);
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SignFormProps>({
    resolver: yupResolver(schemaComplianceForm({ userName })),
    defaultValues: {
      useCase: '',
      criminalRecordsAgreement: 'agree',
      signature: '',
    },
  });

  const onSubmit = async (data: SignFormProps) => {
    try {
      setIsSigning(true);
      setCriminalRecordsAgreement(data?.criminalRecordsAgreement);

      const response = await signForm('POST', data);

      if (response.recomplied) {
        userSettingQuery.mutate({
          setting: 'hide_criminal_records',
          state: grantAccessToAccountWithoutCriminalData,
        });
      } else {
        redirect('/compliance-prohibited');
      }

      reset();
    } catch (err: unknown) {
      if (err instanceof Error) setErrorMessage(err.message);
    }
    setIsSigning(false);
  };

  const complianceReactivationFeatures = `${
    hasCriminalEnabled ? 'criminal, traffic, ' : ''
  }bankruptcy, judgment and lien`;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <QuestionContainter>
        <QuestionTag variant="h4" textAlign="center">
          Question 1
        </QuestionTag>
        <Text variant="h5" fontWeight="normal">
          Please describe how you will use data provided by {brandName}. Please
          note that vague/insufficiently detailed answers may result in your
          account being shut down.
        </Text>

        <Controller
          name="useCase"
          control={control}
          render={({ field }) => (
            <InputTag
              inputRef={field.ref}
              id={field.name}
              data-testid={field.name}
              placeholder="Describe how you will use our data"
              error={!!errors.useCase?.message}
              helperText={errors.useCase?.message}
              inputProps={{ maxLength: 200 }}
              {...field}
            />
          )}
        />
      </QuestionContainter>

      {grantAccessToAccountWithoutCriminalData && (
        <QuestionContainter>
          <QuestionTag variant="h4">Question 2</QuestionTag>
          <Text variant="h5" fontWeight="normal">
            If you need access quickly, we can restore your account{' '}
            <b>without</b> criminal, traffic, bankruptcy, judgment, and lien
            data. Do you agree to turn off that data?
          </Text>

          <FormControl>
            <Controller
              name="criminalRecordsAgreement"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field} ref={null}>
                  <FormControlLabel
                    value="agree"
                    control={<Radio size="small" />}
                    label={
                      <Text variant="h6" fontWeight={500} margin={0}>
                        Yes. Please allow access to my account without this
                        data.
                      </Text>
                    }
                  />
                  <FormControlLabel
                    value="reject"
                    control={<Radio size="small" />}
                    label={
                      <Text variant="h6" fontWeight={500} margin={0}>
                        No. I would like to be able to access this data. (Please
                        be sure your answer to question 1 provides details on
                        how you will be using this data. Our Compliance Team
                        will review your response and notify you of their
                        decision within 2 business days.)
                      </Text>
                    }
                  />
                  <FormControlLabel
                    value="reject-and-refund"
                    control={<Radio size="small" />}
                    label={
                      <Text variant="h6" fontWeight={500} margin={0}>
                        No. Please cancel my account and refund my last payment.
                      </Text>
                    }
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
          <Text variant="h6" fontWeight={300} color="secondary.contrastText">
            <small>
              (Please note: You may still run searches, but{' '}
              {complianceReactivationFeatures} data will be turned off until
              your response to Question 1 is reviewed by the Compliance Team.
              You will be notified of their decision within 2 business days.).
            </small>
          </Text>
        </QuestionContainter>
      )}

      <QuestionContainter>
        <QuestionTag variant="h4">
          Question {grantAccessToAccountWithoutCriminalData ? '3' : '2'}
        </QuestionTag>
        <Text variant="h5" fontWeight="normal">
          Certify the following statement by typing in your full name below, as
          it appears on your account, which will act as your digital signature.
        </Text>

        <SignatureLabelTag
          variant="h6"
          width="100%"
          fontWeight="300"
          fontStyle="italic"
        >
          I hereby certify that the above statements are true and I am not using{' '}
          {brandName} for any of the prohibited uses stated in the{' '}
          <Link
            href={tosLink}
            target="_blank"
            fontWeight="bold"
            underline="always"
            color="success.main"
          >
            Terms of Service
          </Link>
          . I also certify that I am solely responsible for all account related
          decisions.
        </SignatureLabelTag>

        <Controller
          name="signature"
          control={control}
          render={({ field }) => (
            <InputTag
              id={field.name}
              data-testid={field.name}
              inputRef={field.ref}
              type="text"
              placeholder={`Type ${userName}`}
              error={!!errors.signature?.message}
              helperText={errors.signature?.message}
              {...field}
            />
          )}
        />
      </QuestionContainter>

      {errorMessage && (
        <Box sx={{ textAlign: 'center' }}>
          <Text variant="h6" color="error">
            <Icons.ErrorOutline
              sx={{ width: 20, position: 'relative', top: 9 }}
            />{' '}
            {errorMessage}
          </Text>
        </Box>
      )}

      <ComplianceFormFooter isSigning={isSigning} />
    </form>
  );
}
